/**
 * 通用「布局」设置：位置、画布对齐与翻转、旋转角度、3D 旋转。
 */
import type { ChangeEvent } from 'react';
import {
  Button,
  ButtonGroup,
  cn,
  InputGroup,
  Label,
} from '@heroui/react';
import {
  IconBoxAlignBottom,
  IconBoxAlignLeft,
  IconBoxAlignRight,
  IconBoxAlignTop,
  IconBoxMargin,
  IconFlipHorizontal,
  IconFlipVertical,
  IconRotate3d,
} from '@tabler/icons-react';
import type { Element } from '@/components/canvas/document/publicTypes';

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** 相对画布对齐：由父组件根据画布宽高与元素宽高计算 x/y */
export type CanvasAlignAction = 'top' | 'bottom' | 'left' | 'right' | 'center';

export interface LayoutSettingsValue {
  x: number;
  y: number;
  rotation?: number;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  rotate3dX?: number;
  rotate3dY?: number;
  rotate3dZ?: number;
}

export interface LayoutSettingsProps {
  className?: string;
  title?: string;
  value: LayoutSettingsValue;
  onChange: (patch: Partial<LayoutSettingsValue>) => void;
  onAlignToCanvas?: (action: CanvasAlignAction) => void;
  sections?: Partial<{
    alignAndFlip: boolean;
    rotation: boolean;
    threeD: boolean;
  }>;
}

const SECTION_DEFAULT = {
  alignAndFlip: true,
  rotation: true,
  threeD: true,
} as const;

/** 从元素读出布局面板值 */
export function layoutSettingsValueFromElement(
  element: Pick<Element, 'layout'>,
): LayoutSettingsValue {
  return {
    x: element.layout.x,
    y: element.layout.y,
    rotation: element.layout.rotation,
    flipHorizontal: Boolean(element.layout.flipX),
    flipVertical: Boolean(element.layout.flipY),
    rotate3dX: num(element.layout.rotate3d?.x),
    rotate3dY: num(element.layout.rotate3d?.y),
    rotate3dZ: num(element.layout.rotate3d?.z),
  };
}

export function LayoutSettings({
  className,
  title = '布局',
  value,
  onChange,
  onAlignToCanvas,
  sections,
}: LayoutSettingsProps) {
  const sec = { ...SECTION_DEFAULT, ...sections };
  const alignDisabled = !onAlignToCanvas;

  const rotation = value.rotation ?? 0;
  const flipH = value.flipHorizontal ?? false;
  const flipV = value.flipVertical ?? false;
  const d3x = value.rotate3dX ?? 0;
  const d3y = value.rotate3dY ?? 0;
  const d3z = value.rotate3dZ ?? 0;

  return (
    <div className={cn('flex w-full flex-col gap-3 p-1', className)}>
      <p className="text-sm font-medium">{title}</p>

      <div className="flex w-full flex-col gap-2">
        <div className="flex flex-row gap-1">
          <InputGroup aria-label="元素 X 坐标" variant="secondary">
            <InputGroup.Prefix>X</InputGroup.Prefix>
            <InputGroup.Input
              type="number"
              value={value.x}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ x: num(e.target.value) })}
              className="w-full text-center"
            />
          </InputGroup>
          <InputGroup aria-label="元素 Y 坐标" variant="secondary">
            <InputGroup.Prefix>Y</InputGroup.Prefix>
            <InputGroup.Input
              type="number"
              value={value.y}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ y: num(e.target.value) })}
              className="w-full text-center"
            />
          </InputGroup>
        </div>

      </div>

      {sec.alignAndFlip && (
        <div className="flex w-full min-w-0 flex-nowrap items-center gap-1.5">
          <ButtonGroup orientation="horizontal" variant="tertiary" size="sm">
            <Button
              isIconOnly
              isDisabled={alignDisabled}
              aria-label="画布中居顶"
              onPress={() => onAlignToCanvas?.('top')}
            >
              <IconBoxAlignTop className="size-3.5" stroke={1.5} />
            </Button>
            <Button
              isIconOnly
              isDisabled={alignDisabled}
              aria-label="画布中居底"
              onPress={() => onAlignToCanvas?.('bottom')}
            >
              <IconBoxAlignBottom className="size-3.5" stroke={1.5} />
            </Button>
            <Button
              isIconOnly
              isDisabled={alignDisabled}
              aria-label="画布中居左"
              onPress={() => onAlignToCanvas?.('left')}
            >
              <IconBoxAlignLeft className="size-3.5" stroke={1.5} />
            </Button>
            <Button
              isIconOnly
              isDisabled={alignDisabled}
              aria-label="画布中居右"
              onPress={() => onAlignToCanvas?.('right')}
            >
              <IconBoxAlignRight className="size-3.5" stroke={1.5} />
            </Button>
            <Button
              isIconOnly
              isDisabled={alignDisabled}
              aria-label="画布中居中"
              onPress={() => onAlignToCanvas?.('center')}
            >
              <IconBoxMargin className="size-3.5" stroke={1.5} />
            </Button>
          </ButtonGroup>
          <ButtonGroup orientation="horizontal" variant="tertiary" size='sm'>
            <Button
              isIconOnly
              aria-label="纵轴反转"
              onPress={() => onChange({ flipHorizontal: !flipH })}
            >
              <IconFlipVertical stroke={1.5} />
            </Button>
            <Button
              isIconOnly
              aria-label="横轴反转"
              onPress={() => onChange({ flipVertical: !flipV })}
            >
              <IconFlipHorizontal stroke={1.5} />
            </Button>
          </ButtonGroup>
        </div>
      )}

      {sec.rotation && (
        <InputGroup aria-label="旋转角度" variant="secondary">
          <InputGroup.Prefix>角度</InputGroup.Prefix>
          <InputGroup.Input
            type="number"
            value={rotation}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ rotation: num(e.target.value) })}
            className="w-full text-center"
          />
          <InputGroup.Suffix>°</InputGroup.Suffix>
        </InputGroup>
      )}

      {sec.threeD && (
        <div className="w-full overflow-hidden rounded-lg border border-divider">
          <div className="flex items-center gap-2 border-b border-divider bg-default-100/60 px-2.5 py-1.5">
            <IconRotate3d className="size-4 shrink-0 text-muted" stroke={1.5} />
            <Label className="m-0 text-xs font-normal text-muted">3D</Label>
          </div>
          <div className="flex w-full divide-x divide-divider">
            <InputGroup className="min-w-0 flex-1">
              <InputGroup.Prefix>X</InputGroup.Prefix>
              <InputGroup.Input
                aria-label="3D 旋转 X 轴（度）"
                type="number"
                value={d3x}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ rotate3dX: num(e.target.value) })}
                className="w-full text-right"
              />
            </InputGroup>
            <InputGroup className="min-w-0 flex-1">
              <InputGroup.Prefix>Y</InputGroup.Prefix>
              <InputGroup.Input
                aria-label="3D 旋转 Y 轴（度）"
                type="number"
                value={d3y}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ rotate3dY: num(e.target.value) })}
                className="w-full text-right"
              />
            </InputGroup>
            <InputGroup className="min-w-0 flex-1">
              <InputGroup.Prefix>Z</InputGroup.Prefix>
              <InputGroup.Input
                aria-label="3D 旋转 Z 轴（度）"
                type="number"
                value={d3z}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ rotate3dZ: num(e.target.value) })}
                className="w-full text-right"
              />
            </InputGroup>
          </div>
        </div>
      )}
    </div>
  );
}
